import { useState } from 'react';
import { auth } from './firebase';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  GithubAuthProvider,
} from 'firebase/auth';
import { trackEvent } from './analytics';

const providers = {
  google: () => new GoogleAuthProvider(),
  apple: () => {
    const p = new OAuthProvider('apple.com');
    p.addScope('email');
    p.addScope('name');
    return p;
  },
  github: () => new GithubAuthProvider(),
};

// Auth provider flow detection (2026-05-11 evolution):
//
//   Round 1: User hit auth/popup-blocked on iOS — we switched to
//            signInWithRedirect on all touch devices.
//   Round 2: User on Arc Search iOS hit Google's 403 disallowed_useragent
//            because Arc's wrapper UA is on Google's blocklist when the
//            request arrives at accounts.google.com via whole-tab redirect.
//            Popup flows work in Arc because window.open routes through
//            Safari View Controller (trusted UA).
//
// Conclusion: popup-first is the right default — it works in Safari, Arc,
// Chrome iOS, Firefox iOS, AND desktop. Only fall back to redirect when
// popup actually returns auth/popup-blocked (handled in catch block).
//
// Exception: standalone PWA mode (installed-to-homescreen) has no system
// browser available for popups; must use redirect. display-mode:standalone
// detects that.
function shouldUseRedirectFlow() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true; // iOS PWA
  return false;
}

// Detect browsers Google's accounts.google.com refuses to talk to:
// in-app webviews (Instagram, FB, LinkedIn, Twitter, mChatAI iOS, etc.),
// Arc Search, and any non-Safari iOS wrapper. Google returns
// "Error 403: disallowed_useragent" because their UA isn't on Google's
// OAuth allow-list. We can't fix that on Google's side — we tell the user
// + route them to the email/password form (works everywhere).
//
// Detection strategy:
//   1. Explicit wrapper tags (Arc, Instagram, FB, etc.) → block.
//   2. iOS WKWebView fingerprint: iPhone/iPad UA *without* a Safari/X.Y
//      version tag at the end → it's an embedded WebView, not Safari proper.
//      Real Safari sends "...Version/17.6 Mobile/15E148 Safari/604.1".
//      WKWebView sends "...Mobile/15E148" with no Safari/ at the end.
//      This catches the mChatAI iOS in-app browser + every other generic
//      WKWebView wrapper.
function isDisallowedOAuthBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Explicit blocklist (Arc + social in-app).
  if (/Arc[A-Za-z]*\//.test(ua)) return true;
  if (/(Instagram|FBAN|FBAV|FB_IAB|Twitter|Line|MicroMessenger|LinkedIn|TikTok|Snapchat)/i.test(ua)) return true;
  // iOS WKWebView without Safari/ tag = embedded webview (incl. mChatAI iOS).
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  if (isIOS && !/Safari\//.test(ua)) return true;
  return false;
}

export default function SignInModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');

  const handleProvider = async (providerKey) => {
    setLoading(true);
    setError('');
    try {
      const provider = providers[providerKey]();
      if (shouldUseRedirectFlow()) {
        // Whole-tab navigation — the user lands back on the same URL after
        // the IdP returns. getRedirectResult fires in App.js → AuthContext
        // on the next page load and completes sign-in.
        await signInWithRedirect(auth, provider);
        // No onClose() — the page is navigating away.
      } else {
        const result = await signInWithPopup(auth, provider);
        try {
          const info = getAdditionalUserInfo(result);
          trackEvent('login', { method: providerKey, is_new_user: !!(info && info.isNewUser) });
        } catch (_) { /* analytics best-effort */ }
        onClose();
      }
    } catch (err) {
      // popup-blocked on a desktop edge case: fall back to redirect.
      if (err.code === 'auth/popup-blocked') {
        try {
          await signInWithRedirect(auth, providers[providerKey]());
          return;
        } catch (redirErr) {
          console.error(`[Auth] redirect fallback failed:`, redirErr.code, redirErr.message);
          setError(`${redirErr.code}: ${redirErr.message}`);
          return;
        }
      }
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        console.error(`[Auth] ${providerKey} sign-in error:`, err.code, err.message);
        setError(`${err.code}: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Enter your email above, then click Forgot password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
    } catch {
      // Don't leak whether the email exists
      setResetSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isSignUp) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        // Send email verification
        await sendEmailVerification(result.user).catch(() => {});
        setVerifyNote('Check your email to verify your account.');
        trackEvent('login', { method: 'email', is_new_user: true });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        trackEvent('login', { method: 'email', is_new_user: false });
      }
      onClose();
    } catch (err) {
      const msg = err.code === 'auth/user-not-found' ? 'No account with that email. Try signing up.'
        : err.code === 'auth/wrong-password' ? 'Incorrect password.'
        : err.code === 'auth/email-already-in-use' ? 'Email already in use. Try signing in.'
        : err.code === 'auth/weak-password' ? 'Password must be at least 6 characters.'
        : err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1e293b', borderRadius: 12, padding: '32px 28px',
          width: 340, maxWidth: '90vw', color: '#f8fafc',
        }}
      >
        <h2 style={{ margin: '0 0 6px', fontSize: '1.2rem', fontWeight: 600 }}>
          Sign in to mChatAI
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: '0 0 16px' }}>
          Your mChatAI email works on every platform — macOS, iOS, Android, Windows, and web.
        </p>

        {/* Email/password form — universal login */}
        <form onSubmit={handleEmail} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <input
            type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            required style={inputStyle}
          />
          <input
            type="password" placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            required minLength={6} style={inputStyle}
          />
          {!isSignUp && !resetSent && (
            <button type="button" onClick={handleForgotPassword} disabled={loading}
              style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.78rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
              Forgot password?
            </button>
          )}
          {resetSent && (
            <div style={{ color: '#10b981', fontSize: '0.8rem' }}>
              Password reset link sent. Check your email.
            </div>
          )}
          {verifyNote && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: 8, padding: '8px 12px', color: '#f59e0b', fontSize: '0.8rem',
            }}>
              Verification email sent to {email}. Verify to unlock cloud features on all platforms.
            </div>
          )}
          {error && (
            <div style={{ color: '#f87171', fontSize: '0.8rem' }}>{error}</div>
          )}
          <button type="submit" disabled={loading} style={{
            ...oauthBtnStyle, background: '#3b82f6', color: '#fff', border: 'none',
          }}>
            {loading ? 'Please wait...' : isSignUp ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', margin: '0 0 12px' }}>
          or continue with
        </div>

        {/* OAuth providers — Google's accounts.google.com blocks several
            in-app browsers (Arc, Instagram, FB, etc.) with a 403
            disallowed_useragent. We can't bypass that — show the user a
            helpful note + disable the OAuth buttons in those contexts.
            Email/password works everywhere. */}
        {isDisallowedOAuthBrowser() && (
          <div style={{
            background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
            borderRadius: 8, padding: '10px 12px', marginBottom: 10,
            color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.45,
          }}>
            Social sign-in (Google/Apple/GitHub) isn't supported in this browser.
            Use the email form above, or open mchatai.com in Safari.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => handleProvider('google')} disabled={loading || isDisallowedOAuthBrowser()}
            style={oauthBtnStyle}>
            <GoogleIcon /> Continue with Google
          </button>
          <button onClick={() => handleProvider('apple')} disabled={loading || isDisallowedOAuthBrowser()}
            style={oauthBtnStyle}>
            <AppleIcon /> Continue with Apple
          </button>
          <button onClick={() => handleProvider('github')} disabled={loading || isDisallowedOAuthBrowser()}
            style={oauthBtnStyle}>
            <GitHubIcon /> Continue with GitHub
          </button>
        </div>

        <button
          onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
          style={{
            background: 'none', border: 'none', color: '#3b82f6',
            fontSize: '0.8rem', cursor: 'pointer', marginTop: 12, padding: 0,
          }}
        >
          {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
      </div>
    </div>
  );
}

const oauthBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  padding: '10px 16px', borderRadius: 8, border: '1px solid #334155',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem',
  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
};

const inputStyle = {
  padding: '10px 12px', borderRadius: 8, border: '1px solid #334155',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem',
  fontFamily: 'inherit', outline: 'none',
};

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 17 17" fill="currentColor">
      <path d="M12.15 8.48c-.02-2.01 1.64-2.98 1.71-3.02-.93-1.36-2.38-1.55-2.9-1.57-1.23-.12-2.4.72-3.03.72-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.3 2-.84 1.45-.64 4.17.66 5.83.44.63.96 1.35 1.65 1.32.66-.03.91-.43 1.71-.43s1.02.43 1.72.41c.71-.01 1.16-.65 1.6-1.28.5-.73.71-1.44.72-1.48-.02-.01-1.38-.53-1.4-2.1z"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}
